// M2 achievements (Gusher & Rotary). check(state) is pure and cheap; false on an empty state.
// State the sim must maintain for these (docs/M2_SPEC.md §12):
//   well.gushed, well.prod.qi, well.prod.rate, well.status, well.fieldId, well.spudDay, well.depth, well.eLogged
//   well.strikeDay (day pay was first reached), well.strikeRigCond (rig condition % at strike)
//   company.stats.hotOilBbl, company.stats.bopSaves, company.stats.expropriations
//   research.done[techId].mode === 'patent'
import { ownWells } from '../achWells.js';
import { dateToDay } from '../../core/time.js';

const wells = ownWells; // Quick Start wells don't count (polish G7); shared per tick (achWells.js)
const producing = (w) => !!w.prod || w.status === 'PRODUCING' || w.status === 'COMPLETING';
const stat = (s, k) => s?.company?.stats?.[k] ?? 0;

const D1931 = dateToDay(1931, 1, 1);
const D1932 = dateToDay(1932, 1, 1);
const D1933 = dateToDay(1933, 1, 1);
const D1946 = dateToDay(1946, 1, 1);

export const M2_ACHIEVEMENTS = [
  { id: 'spindletop', name: 'Spindletop!', icon: 'gusher', era: 'gusher',
    desc: 'Bring in a gusher on the hill at Beaumont.',
    check: (s) => wells(s).some((w) => w.fieldId === 'spindletop' && (w.gushed || w.status === 'GUSHER')) },
  { id: 'hot_oil', name: 'Hot Oil', icon: 'flame', era: 'gusher',
    desc: 'Run oil past the Railroad Commission’s allowable. Somebody has to.',
    check: (s) => stat(s, 'hotOilBbl') > 0 },
  { id: 'rock_bit_baron', name: 'Rock Bit Baron', icon: 'cone', era: 'gusher',
    desc: 'Patent the two-cone rock bit before Howard Hughes does.',
    check: (s) => s?.research?.done?.two_cone_bit?.mode === 'patent' },
  { id: 'shut_it_in', name: 'Shut It In', icon: 'valve', era: 'gusher',
    desc: 'Catch a kick with a blowout preventer and kill the well.',
    check: (s) => stat(s, 'bopSaves') >= 1 },
  { id: 'east_texas_wildcatter', name: 'Wildcatter of East Texas', icon: 'derrick', era: 'gusher',
    desc: 'Strike oil in East Texas before 1931, while the majors’ geologists are still saying no.',
    check: (s) => wells(s).some((w) => w.fieldId === 'east_texas' && producing(w) && (w.strikeDay ?? w.spudDay ?? Infinity) < D1931) },
  { id: 'dad_joiner', name: 'Dad Joiner', icon: 'boiler', era: 'gusher',
    desc: 'Strike oil in East Texas with a rig held together by baling wire (below 40% condition).',
    check: (s) => wells(s).some((w) => w.fieldId === 'east_texas' && producing(w) && (w.strikeRigCond ?? 100) < 40) },
  { id: 'town_lot_tycoon', name: 'Town Lot Tycoon', icon: 'house', era: 'gusher',
    desc: 'Own ten producing wells on Signal Hill.',
    check: (s) => wells(s).filter((w) => w.fieldId === 'signal_hill' && producing(w)).length >= 10 },
  { id: 'potrero', name: 'Potrero del Llano', icon: 'gusher', era: 'gusher',
    desc: 'Bring in a single well flowing 50,000 barrels a day.',
    check: (s) => wells(s).some((w) => Math.max(w.prod?.qi ?? 0, w.prod?.rate ?? 0) >= 50000) },
  { id: 'mr_five_percent', name: 'Mr. Five Percent', icon: 'coin', era: 'gusher',
    desc: 'Produce oil at Kirkuk. Calouste Gulbenkian would approve.',
    check: (s) => wells(s).some((w) => w.fieldId === 'kirkuk' && producing(w)) },
  { id: 'expropriated', name: 'Expropriated', icon: 'flag', era: 'gusher',
    desc: 'Lose your assets to a government that wants its oil back.',
    check: (s) => stat(s, 'expropriations') >= 1 },
  { id: 'ten_thousand_feet', name: 'Ten Thousand Feet', icon: 'drill', era: 'gusher',
    desc: 'Drill a well past 10,000 feet before 1946.',
    check: (s) => (s?.day ?? Infinity) < D1946 && wells(s).some((w) => (w.depth ?? 0) >= 10000) },
  { id: 'pechelbronn', name: 'Pechelbronn', icon: 'wave', era: 'gusher',
    desc: 'Run an electric log on one of your wells.',
    check: (s) => wells(s).some((w) => w.eLogged) },
  { id: 'ten_cent_crude', name: 'Ten-Cent Crude', icon: 'barrel', era: 'gusher',
    desc: 'Come through the East Texas collapse: be solvent in 1932 with a producing Texas well.',
    check: (s) => (s?.day ?? 0) >= D1932 && (s?.day ?? 0) < D1933 && (s?.company?.cash ?? -1) > 0
      && wells(s).some((w) => producing(w) && ['spindletop', 'sour_lake', 'batson', 'humble', 'ranger', 'burkburnett', 'yates', 'east_texas'].includes(w.fieldId)) },
];
